import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Container, Grid, Typography, Box } from "@mui/material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { STATUS } from "../../../../../redux/constants";
import { resetAddCombustionStatus } from "../../../../../redux/actions";
import useStyles from "./styles";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CeroItemPair from "../../../../../components/CeroItemPair";

const EndOfLifeTreatment = (props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { emissionData, onCancel } = props;

  const deleteEmissionData = useSelector(
    (state) => state.emission.deleteEmissions
  );

  useEffect(() => {
    if (deleteEmissionData.status === STATUS.SUCCESS) {
      enqueueSnackbar(
        "End-of-life treatment for sold products record deleted successfully",
        {
          variant: "success",
        }
      );
      dispatch(resetAddCombustionStatus());
      onCancel();
    } else if (deleteEmissionData.status === STATUS.ERROR) {
      enqueueSnackbar(
        deleteEmissionData.message.message ||
          "We couldn't process your request. Please try again later.",
        { variant: "error" }
      );
      dispatch(resetAddCombustionStatus());
    }
  }, [deleteEmissionData, enqueueSnackbar, onCancel, dispatch]);

  //   end_of_life_treatment
  const monthDict = {
    1: "January",
    2: "February",
    3: "March",
    4: "April",
    5: "May",
    6: "June",
    7: "July",
    8: "August",
    9: "September",
    10: "October",
    11: "November",
    12: "December",
  };

  return (
    <>
      <div className={classes.headerContainer}>
        <div
          className={classes.backContainer}
          onClick={() => {
            navigate(-1);
          }}
        >
          <ArrowBackIosIcon />
          Back
        </div>
      </div>
      <Container className={classes.container}>
        <Box className={classes.innerContainer}>
          <Box className={classes.header}>
            <Typography variant="h6" component="div">
              End-of-life treatment of sold products
            </Typography>
          </Box>
          <Box className={classes.topContainer}>
            <Grid
              container
              direction={"row"}
              wrap="nowrap"
              justifyContent={"space-between"}
              display={"contents"}
              spacing={12}
            >
              <Grid
                item
                container
                direction={"column"}
                display={"contents"}
                xs={10}
              >
                <Box className={classes.actionBox}></Box>
                <CeroItemPair
                  title="Facility:"
                  value={
                    emissionData.facility_name
                      ? emissionData.facility_name
                      : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />

                <CeroItemPair
                  title="Month and Year:"
                  value={
                    emissionData.year && emissionData.month
                      ? monthDict[emissionData.month] + " " + emissionData.year
                      : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />
                <CeroItemPair
                  title="Product:"
                  value={
                    emissionData.product_name ? emissionData.product_name : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />
                <CeroItemPair
                  title="Total Mass of Sold Products & Packaging:"
                  value={
                    emissionData.total_mass ? emissionData.total_mass : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />
                <CeroItemPair
                  title="Unit:"
                  value={emissionData.unit ? emissionData.unit : "NA"}
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />
                <CeroItemPair
                  title="Percentage of total waste treated by method:"
                  value={
                    emissionData.percentage_treated
                      ? emissionData.percentage_treated
                      : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />
                <CeroItemPair
                  title="Material:"
                  value={
                    emissionData.material_name
                      ? emissionData.material_name
                      : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />
                <CeroItemPair
                  title="Waste treatment method:"
                  value={
                    emissionData.waste_treatment_method_name
                      ? emissionData.waste_treatment_method_name
                      : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />
                {emissionData?.audited_by && (
                  <>
                    <CeroItemPair
                      title="Audited by:"
                      value={
                        emissionData.audited_by ? emissionData.audited_by : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Audited on:"
                      value={
                        emissionData.audited_on
                          ? dayjs(emissionData.audited_on).format("DD/MM/YYYY")
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box className={classes.bottomContainer}>
            <Typography
              variant="h6"
              component="h6"
              className={classes.previewTitle}
            >
              Emission
            </Typography>

            <Grid container direction="row" spacing={4}>
              {/* LEFT COLUMN */}
              <Grid item container direction="column" xs={12} md={6}>
                {emissionData?.co2 != null && (
                  <Typography className={classes.previewItem}>
                    CO<sub>2</sub>: {emissionData.co2} tonnes
                  </Typography>
                )}
                {emissionData?.ch4 != null && (
                  <Typography className={classes.previewItem}>
                    CH<sub>4</sub>: {emissionData.ch4} tonnes
                  </Typography>
                )}
                {emissionData?.biofuel_co2 != null && (
                  <Typography className={classes.previewItem}>
                    BioFuel CO<sub>2</sub>: {emissionData.biofuel_co2} tonnes
                  </Typography>
                )}
                {emissionData?.co2e != null && (
                  <Typography className={classes.previewItem}>
                    CO<sub>2</sub>e: {emissionData.co2e} tonnes
                  </Typography>
                )}
              </Grid>

              {/* RIGHT COLUMN */}
              <Grid item container direction="column" xs={12} md={6}>
                {emissionData?.n2o != null && (
                  <Typography className={classes.previewItem}>
                    N<sub>2</sub>O: {emissionData.n2o} tonnes
                  </Typography>
                )}
                {emissionData?.ef != null && (
                  <Typography className={classes.previewItem}>
                    EF: {emissionData.ef} kgCO<sub>2</sub>e/unit
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default EndOfLifeTreatment;
